import Cookies from 'js-cookie';

import { ALL_ATTRIBUTION_KEYS, SIGNUP_ATTRIBUTION_KEYS } from '@/shared/constants/attribution';
import { LS_UTM_PARAMS } from '@/shared/constants/localStorage';

import { isTrackingDisabled } from './isTrackingDisabled';

export type AttributionParams = Partial<Record<(typeof ALL_ATTRIBUTION_KEYS)[number], string>>;

export type GA4Context = {
  clientId: string;
  sessionId: string;
};

/**
 * Type sent to backend on signup. Backend uses set-once semantics —
 * fields not present in payload are not touched on existing user records.
 */
export type AttributionPayloadForSignup = {
  [K in (typeof SIGNUP_ATTRIBUTION_KEYS)[number] as `first_${K}`]?: string;
} & {
  ga4_client_id?: string;
  ga4_session_id?: string;
};

const GA4_SESSION_COOKIE_NAME = '_ga_R60NZTEKKL';

const filterAttributionParams = (value: unknown): AttributionParams => {
  if (!value || typeof value !== 'object') return {};

  const filtered: AttributionParams = {};
  for (const key of ALL_ATTRIBUTION_KEYS) {
    const candidate = (value as Record<string, unknown>)[key];
    if (typeof candidate === 'string' && candidate) filtered[key] = candidate;
  }
  return filtered;
};

const captureFromSearch = (search: string): AttributionParams => {
  const urlParams = new URLSearchParams(search);
  const params: AttributionParams = {};
  for (const key of ALL_ATTRIBUTION_KEYS) {
    const value = urlParams.get(key);
    if (value) params[key] = value;
  }
  return params;
};

export const buildAttributionSnapshot = ({ search }: { search: string }): AttributionParams =>
  captureFromSearch(search);

/**
 * Read stored attribution params from localStorage and re-apply the allowlist.
 * Falls back to empty object if nothing is stored or JSON is invalid.
 */
export const getStoredAttributionParams = (): AttributionParams => {
  try {
    const stored = window.localStorage.getItem(LS_UTM_PARAMS);
    return stored ? filterAttributionParams(JSON.parse(stored)) : {};
  } catch {
    return {};
  }
};

/**
 * Capture the filtered campaign snapshot from URL into localStorage. URL
 * attribution is always a complete new snapshot. Returns it for immediate use.
 */
export const captureAttributionParamsFromUrl = (): AttributionParams => {
  if (isTrackingDisabled()) return {};

  const captured = captureFromSearch(window.location.search);
  if (Object.keys(captured).length === 0) return {};

  const params = buildAttributionSnapshot({
    search: window.location.search,
  });

  try {
    window.localStorage.setItem(LS_UTM_PARAMS, JSON.stringify(params));
  } catch {}

  return params;
};

/**
 * Read GA4 client_id from the _ga cookie.
 * Format: GA1.1.XXXXXXXXXX.XXXXXXXXXX → client_id = last two parts joined by dot.
 */
export const getGA4ClientId = (): string | undefined => {
  try {
    const gaCookie = Cookies.get('_ga');
    if (!gaCookie) return undefined;

    const parts = gaCookie.split('.');

    if (parts.length >= 4) {
      return `${parts[2]}.${parts[3]}`;
    }
  } catch {}

  return undefined;
};

export const getGA4SessionId = (): string | undefined => {
  try {
    const value = Cookies.get(GA4_SESSION_COOKIE_NAME);
    if (!value) return undefined;

    const gs2SessionId = /\.s(\d+)(?:\$|$)/.exec(value)?.[1];
    if (gs2SessionId) return gs2SessionId;

    return /^GS\d+\.\d+\.(\d+)(?:\.|$)/.exec(value)?.[1];
  } catch {}

  return undefined;
};

export const getGA4Context = (): GA4Context | undefined => {
  if (isTrackingDisabled()) return undefined;

  const clientId = getGA4ClientId();
  const sessionId = getGA4SessionId();
  return clientId && sessionId ? { clientId, sessionId } : undefined;
};

/**
 * Assemble attribution payload for the signup API call.
 * Includes only fields with values — backend uses set-once semantics,
 * so missing fields don't overwrite existing values on the user record.
 */
export const getAttributionPayloadForSignup = (): AttributionPayloadForSignup => {
  const stored = getStoredAttributionParams();
  const payload: AttributionPayloadForSignup = {};

  for (const key of SIGNUP_ATTRIBUTION_KEYS) {
    const value = stored[key];
    if (value) {
      (payload as Record<string, string>)[`first_${key}`] = value;
    }
  }

  const clientId = getGA4ClientId();
  if (clientId) payload.ga4_client_id = clientId;

  const sessionId = getGA4SessionId();
  if (sessionId) payload.ga4_session_id = sessionId;

  return payload;
};
